import { useEffect } from "react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import LegalNav from "../components/site/LegalNav";
import { DRAFT_NOTICE, SUPPORT_EMAIL } from "../content/legal";

const Block = ({ block }) => {
  if (block.t === "list") {
    return (
      <ul className="mt-4 list-disc space-y-2.5 pl-6 marker:text-neutral-400">
        {block.items.map((item, i) =>
          typeof item === "string" ? (
            <li key={i}>{item}</li>
          ) : (
            <li key={i}>
              <strong className="font-bold text-black">{item.lead}</strong>{" "}
              {item.text}
            </li>
          ),
        )}
      </ul>
    );
  }
  if (block.t === "ol") {
    return (
      <ol className="mt-4 list-decimal space-y-2.5 pl-6 marker:text-neutral-400">
        {block.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ol>
    );
  }
  if (block.t === "email") {
    return (
      <p className="mt-8">
        Questions?{" "}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          data-testid="contact-email-link"
          className="font-bold text-black underline underline-offset-4 transition-opacity hover:opacity-70"
        >
          {SUPPORT_EMAIL}
        </a>
      </p>
    );
  }
  return (
    <p
      className={`mt-4 ${
        block.strong ? "font-bold text-black" : ""
      } ${block.small ? "text-[14px] text-[#7C7C7C]" : ""}`}
    >
      {block.text}
    </p>
  );
};

export default function LegalPage({ doc }) {
  useEffect(() => {
    document.title = `${doc.title} — Vibealone`;
    return () => {
      document.title = "Vibealone — Discover. Experience. Act.";
    };
  }, [doc]);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <main className="flex-1" data-testid={`legal-page-${doc.slug.slice(1)}`}>
        <div className="mx-auto w-full max-w-[1366px] px-5 pb-24 pt-[clamp(36px,5vw,72px)] md:px-[77px]">
          <div className="grid grid-cols-[minmax(0,1fr)] gap-10 md:grid-cols-[220px_minmax(0,1fr)] md:gap-20">
            <LegalNav />
            <article className="min-w-0 max-w-[720px] font-glacial text-[16.5px] leading-[1.75] text-[#1a1a1a]">
              <h1
                data-testid="legal-title"
                className="font-spartan text-[32px] font-bold leading-tight text-black md:text-[40px]"
              >
                {doc.title}
              </h1>
              <p
                data-testid="legal-meta"
                className="mt-3 font-glacial text-[14px] text-[#7C7C7C]"
              >
                {doc.meta}
              </p>
              <div
                data-testid="legal-draft-notice"
                className="mt-6 rounded-xl bg-[#F7F6F4] px-5 py-4 font-glacial text-[14px] leading-[1.6] text-[#444444]"
              >
                <p className="font-spartan text-[15px] font-bold text-black">
                  {DRAFT_NOTICE.heading}
                </p>
                {DRAFT_NOTICE.paragraphs.map((line, i) => (
                  <p key={i} className="mt-2">
                    {line.includes(SUPPORT_EMAIL) ? (
                      <>
                        {line.split(SUPPORT_EMAIL)[0]}
                        <a
                          href={`mailto:${SUPPORT_EMAIL}`}
                          data-testid="draft-notice-email-link"
                          className="font-bold text-black underline underline-offset-4 transition-opacity hover:opacity-70"
                        >
                          {SUPPORT_EMAIL}
                        </a>
                        {line.split(SUPPORT_EMAIL)[1]}
                      </>
                    ) : (
                      line
                    )}
                  </p>
                ))}
              </div>
              <p className="mt-8 text-[17px]">{doc.intro}</p>

              {doc.sections.map((section, i) => (
                <section key={i} className="mt-10">
                  {section.heading && (
                    <h2 className="font-spartan text-[20px] font-bold text-black">
                      {section.heading}
                    </h2>
                  )}
                  {section.blocks.map((block, j) => (
                    <Block key={j} block={block} />
                  ))}
                </section>
              ))}
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
